import React from 'react';
import { InputField } from '../ds/InputField';
import { ComboBox } from '../ds/ComboBox';
import { clientComboOptions } from '../utils/clientOptions';
import { clientsApi } from '../services/api';
import type { Client } from '../services/api';
import { emailError } from '../utils/clientFields';

/**
 * A12 (uwagi Bronka, raport 2026-10-19): „przy planowaniu — Zarządca + e-mail". Opcjonalny zarządca w oknach
 * „Zaplanuj przegląd" i „Zaplanuj dla zaznaczonych": wybór z listy, pod spodem jego e-mail; gdy zarządca nie ma
 * e-maila — pole do wpisania, zapisywane u zarządcy (PUT /clients/:id, COALESCE — inne pola nietknięte).
 * Przypięcie budynków idzie istniejącą trasą „Przypisz zarządcę" (POST /clients/:id/attach-buildings).
 */
export interface PlanClientValue {
  clientId: string;
  email: string;
}

export const EMPTY_PLAN_CLIENT: PlanClientValue = { clientId: '', email: '' };

const hasEmail = (c: Client | undefined) => !!(c && c.email && c.email.trim());

/** Błąd pola e-mail (tylko gdy wybrany zarządca nie ma e-maila i coś wpisano) albo null. */
export function planClientError(value: PlanClientValue, clients: Client[]): string | null {
  if (!value.clientId) return null;
  const c = clients.find((x) => x.id === value.clientId);
  if (hasEmail(c)) return null;
  return emailError(value.email);
}

export interface PlanClientOutcome {
  clientName: string;
  attached: number;
  reassigned: number;
  emailSaved: boolean;
  /** Budynki z INNYM zarządcą, których nie przepięto (bez `reassign`) — do pytania „Zmienić zarządcę?". */
  others: { id: string; address: string; clientName: string | null }[];
}

/**
 * Zapis zarządcy po zaplanowaniu: najpierw e-mail (jeśli wpisany i zarządca go nie miał), potem przypięcie
 * budynków. `reassign` = świadoma zmiana obecnego zarządcy (okno pojedynczego budynku pokazuje go wprost).
 * Rzuca błąd API — wołający pokazuje go obok wyniku planowania (przegląd już jest zapisany).
 */
export async function savePlanClient(
  value: PlanClientValue,
  clients: Client[],
  buildingIds: string[],
  { reassign = false }: { reassign?: boolean } = {},
): Promise<PlanClientOutcome | null> {
  if (!value.clientId || buildingIds.length === 0) return null;
  const c = clients.find((x) => x.id === value.clientId);
  const clientName = c ? (c.shortName || c.name) : 'wybrany zarządca';
  let emailSaved = false;
  const email = value.email.trim();
  if (email && !hasEmail(c)) {
    await clientsApi.update(value.clientId, { email });
    emailSaved = true;
  }
  const res = await clientsApi.attachBuildings(value.clientId, reassign ? { buildingIds, reassign: true } : { buildingIds });
  const others = res.skipped
    .filter((x) => x.reason === 'other-client')
    .map((x) => ({
      id: x.id,
      address: `${[x.street, x.houseNumber].filter(Boolean).join(' ')}${x.city ? `, ${x.city}` : ''}`,
      clientName: x.clientName,
    }));
  return { clientName, attached: res.attached, reassigned: res.reassigned ?? 0, emailSaved, others };
}

/** Jedno zdanie o wyniku (do banera/okna wyniku). */
export function planClientSummary(o: PlanClientOutcome | null): string {
  if (!o) return '';
  const emailNote = o.emailSaved ? '; zapisano e-mail zarządcy' : '';
  if (o.attached === 0 && o.others.length === 0) return `Zarządca „${o.clientName}”: bez zmian (już przypisany)${emailNote}.`;
  const parts = [`Zarządca „${o.clientName}”: przypisano ${o.attached}`];
  if (o.reassigned > 0) parts.push(`w tym zmieniony zarządca: ${o.reassigned}`);
  if (o.others.length > 0) parts.push(`z innym zarządcą: ${o.others.length}`);
  return `${parts.join(', ')}${emailNote}.`;
}

export interface PlanClientFieldsProps {
  clients: Client[];
  value: PlanClientValue;
  onChange: (v: PlanClientValue) => void;
  /** Obecny zarządca budynku (okno pojedyncze) — żeby zmiana była widoczna, zanim się wykona. */
  currentClientId?: string | null;
  currentClientName?: string | null;
  disabled?: boolean;
  idPrefix: string;
  /** Okno zbiorcze: budynki z innym zarządcą nie są przepinane bez pytania. */
  bulk?: boolean;
}

const caption: React.CSSProperties = { fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' };

export default function PlanClientFields({
  clients, value, onChange, currentClientId, currentClientName, disabled, idPrefix, bulk,
}: PlanClientFieldsProps) {
  const selected = clients.find((c) => c.id === value.clientId);
  const changesCurrent = !bulk && !!value.clientId && !!currentClientId && currentClientId !== value.clientId;
  const err = planClientError(value, clients);
  return (
    <div data-testid={`${idPrefix}-client`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)' }}>
      {/* B3 (Bronek 21.10): wpisywanie z podpowiedziami (nazwa / skrót) zamiast długiej listy — ds/ComboBox. */}
      <ComboBox
        id={`${idPrefix}-client-select`}
        label="Zarządca (opcjonalnie)"
        emptyLabel={bulk ? '— bez zmian —' : (currentClientName ? `— bez zmian (${currentClientName}) —` : '— bez zarządcy —')}
        value={value.clientId}
        disabled={disabled}
        options={clientComboOptions(clients)}
        onChange={(clientId) => onChange({ clientId, email: '' })}
      />
      {!bulk && !value.clientId && currentClientName && (
        <span style={caption}>Obecny zarządca: {currentClientName}</span>
      )}
      {changesCurrent && (
        <span data-testid={`${idPrefix}-client-change`} style={{ ...caption, color: 'var(--status-rec)' }}>
          Zarządca budynku zmieni się z „{currentClientName || '—'}” na „{selected ? (selected.shortName || selected.name) : ''}”.
        </span>
      )}
      {bulk && value.clientId && (
        <span style={caption}>Budynki bez zarządcy zostaną przypisane od razu. Jeśli któryś ma innego zarządcę, zapytamy o zmianę.</span>
      )}
      {selected && hasEmail(selected) && (
        <span data-testid={`${idPrefix}-client-email`} style={caption}>E-mail zarządcy: {selected.email}</span>
      )}
      {selected && !hasEmail(selected) && (
        <InputField
          label="E-mail zarządcy (opcjonalnie)"
          type="email"
          value={value.email}
          disabled={disabled}
          placeholder="nazwa@domena.pl"
          hint="Zarządca nie ma e-maila w danych — wpisany zapiszemy w zakładce Zarządcy."
          errorMessage={err || undefined}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
        />
      )}
    </div>
  );
}
